import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from 'react-simple-maps';
import { loadTripBySlug } from '../utils/contentLoader';

const ITALY_GEO = process.env.PUBLIC_URL + '/geo/italy-regions.geo.json';

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const DELTA_ZOOM = 0.5;

// ISTAT region codes highlighted as home
const HOME_CODES = ['03']; // Lombardia

// ISTAT codes of regions visited — highlighted gold on the map and card
const VISITED_CODES = [
  '01', // Piemonte
  '02', // Valle d'Aosta
  '03', // Lombardia (also in HOME_CODES — home takes map color priority)
  '04', // Trentino-Alto Adige
  '05', // Veneto
  '06', // Friuli-Venezia Giulia
  '07', // Liguria
  '08', // Emilia-Romagna
  '09', // Toscana
  '10', // Umbria
  '12', // Lazio (Rome)
  '15', // Campania (Naples)
  '19', // Sicilia (Palermo)
  '20', // Sardegna
];

// ISTAT codes with actual written content — enables "Read story" link
const HAS_CONTENT_CODES = [];

// Geographic locations of the two home bases [longitude, latitude]
const PINS = [
  { name: 'Milan', coordinates: [9.19, 45.46] },
  { name: 'Desenzano', coordinates: [10.54, 45.47] },
];

const REGIONS = [
  { code: '13', name: 'Abruzzo',                slug: 'abruzzo' },
  { code: '17', name: 'Basilicata',             slug: 'basilicata' },
  { code: '18', name: 'Calabria',               slug: 'calabria' },
  { code: '15', name: 'Campania',               slug: 'campania' },
  { code: '08', name: 'Emilia-Romagna',         slug: 'emilia-romagna' },
  { code: '06', name: 'Friuli-Venezia Giulia',  slug: 'friuli-venezia-giulia' },
  { code: '12', name: 'Lazio',                  slug: 'lazio' },
  { code: '07', name: 'Liguria',                slug: 'liguria' },
  { code: '03', name: 'Lombardia',              slug: 'lombardia' },
  { code: '11', name: 'Marche',                 slug: 'marche' },
  { code: '14', name: 'Molise',                 slug: 'molise' },
  { code: '01', name: 'Piemonte',               slug: 'piemonte' },
  { code: '16', name: 'Puglia',                 slug: 'puglia' },
  { code: '20', name: 'Sardegna',               slug: 'sardegna' },
  { code: '19', name: 'Sicilia',                slug: 'sicilia' },
  { code: '09', name: 'Toscana',                slug: 'toscana' },
  { code: '04', name: 'Trentino-Alto Adige',    slug: 'trentino-alto-adige' },
  { code: '10', name: 'Umbria',                 slug: 'umbria' },
  { code: '02', name: "Valle d'Aosta",          slug: 'valle-daosta' },
  { code: '05', name: 'Veneto',                 slug: 'veneto' },
];

export default function ItalyDetail() {
  const trip = useMemo(() => loadTripBySlug('italy'), []);
  const [zoom, setZoom] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const mapHeight = isMobile ? 500 : 720;

  return (
    <div style={{ padding: isMobile ? '32px 16px' : '48px' }}>

      {/* Back link */}
      <Link
        to="/travel"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: 'var(--text-3)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.06em',
          textDecoration: 'none',
          marginBottom: '32px',
          transition: 'color 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
      >
        ← TRAVEL
      </Link>

      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.08em',
            color: 'var(--text-3)',
            textTransform: 'uppercase',
          }}>
            Europe
          </span>
          <span style={{ color: 'var(--border-2)', fontSize: '0.7rem' }}>·</span>
          <span style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6rem',
            color: 'var(--text-3)',
          }}>
            Ongoing
          </span>
        </div>

        <h1 style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          lineHeight: 0.95,
          color: 'var(--text)',
          margin: '0 0 16px',
        }}>
          {trip?.title || 'Italy — Home'}
        </h1>

        {trip?.description && (
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '1.2rem',
            color: 'var(--text-2)',
            margin: 0,
            lineHeight: 1.5,
          }}>
            {trip.description}
          </p>
        )}
      </div>

      <div style={{ height: '1px', background: 'var(--border)', marginBottom: '40px' }} />

      {/* Italy regions map */}
      <div style={{ marginBottom: '48px' }}>
        <div style={{
          position: 'relative',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          overflow: 'hidden',
          background: 'var(--surface)',
          height: `${mapHeight}px`,
        }}>
          {/* Zoom controls */}
          <div style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
            {[
              { label: '+', action: () => setZoom((z) => Math.min(z + DELTA_ZOOM, MAX_ZOOM)) },
              { label: '−', action: () => setZoom((z) => Math.max(z - DELTA_ZOOM, MIN_ZOOM)) },
            ].map(({ label, action }) => (
              <button
                key={label}
                onClick={action}
                style={{
                  width: '28px',
                  height: '28px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text-2)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Legend */}
          <div style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            padding: '10px 14px',
          }}>
            {[
              { color: '#7cb8a0', label: 'Home region' },
              { color: '#c4a24a', label: 'Explored' },
              { color: '#c4a24a', label: '⌂ Home base', isPin: true },
            ].map(({ color, label, isPin }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isPin ? (
                  <svg width="12" height="12" viewBox="0 0 12 12">
                    <circle cx="6" cy="6" r="4" fill={color} stroke="#0d0c10" strokeWidth="1.5" />
                  </svg>
                ) : (
                  <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: color, flexShrink: 0 }} />
                )}
                <span style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.58rem',
                  color: 'var(--text-3)',
                  letterSpacing: '0.05em',
                }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          <p style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.58rem',
            color: 'var(--text-3)',
            margin: 0,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}>
            20 regions · stories coming soon
          </p>

          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ scale: 2300, center: [12.5, 41.8] }} // 12.5, 41.3
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup zoom={zoom} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM}>
              <Geographies geography={ITALY_GEO}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const code = geo.properties.reg_istat_code;
                    const isHome = HOME_CODES.includes(code);
                    const isVisited = VISITED_CODES.includes(code);

                    let fill = '#2a2835';
                    if (isHome) fill = '#7cb8a0';
                    else if (isVisited) fill = '#c4a24a';

                    const isClickable = isVisited;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke="#1a1820"
                        strokeWidth={0.4}
                        style={{
                          default: { outline: 'none' },
                          hover: {
                            outline: 'none',
                            fill: isHome
                              ? '#9dd4bc'
                              : isVisited
                              ? '#d4b86a'
                              : '#3a3650',
                            cursor: isClickable ? 'pointer' : 'default',
                          },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>

              {/* Home base pins */}
              {PINS.map((pin) => (
                <Marker key={pin.name} coordinates={pin.coordinates}>
                  <circle
                    r={isMobile ? 4 : 5}
                    fill="#c4a24a"
                    stroke="#0d0c10"
                    strokeWidth={1.5}
                    style={{ pointerEvents: 'none' }}
                  />
                  <text
                    textAnchor="middle"
                    y={isMobile ? -7 : -9}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: isMobile ? '5px' : '6px',
                      fill: 'var(--text)',
                      pointerEvents: 'none',
                    }}
                  >
                    {pin.name}
                  </text>
                </Marker>
              ))}
            </ZoomableGroup>
          </ComposableMap>
        </div>
        <p style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontStyle: 'italic',
          fontSize: '0.85rem',
          color: 'var(--text-3)',
          marginTop: '10px',
          textAlign: 'center',
        }}>
          The peninsula that shaped everything — zoom in to explore each region.
        </p>
      </div>

      {/* Region cards */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '20px',
        }}>
          <div className="eyebrow">Regions</div>
          <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '10px',
        }}>
          {REGIONS.map((region) => {
            const isHome = HOME_CODES.includes(region.code);
            const isVisited = VISITED_CODES.includes(region.code);
            const hasStory = HAS_CONTENT_CODES.includes(region.code);

            return (
              <div
                key={region.code}
                className="card-hover"
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '14px',
                  borderLeft: `3px solid ${
                    isHome ? '#7cb8a0' : isVisited ? 'var(--gold)' : 'var(--border-2)'
                  }`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <h3 style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: '1.05rem',
                    fontWeight: 600,
                    color: 'var(--text)',
                    margin: 0,
                    letterSpacing: '-0.01em',
                  }}>
                    {region.name}
                  </h3>
                  {isHome && (
                    <span style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.55rem',
                      color: '#7cb8a0',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}>
                      home
                    </span>
                  )}
                </div>
                <p style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.58rem',
                  color: 'var(--text-3)',
                  margin: 0,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}>
                  {hasStory ? 'Read story →' : 'Story coming soon'}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom back link */}
      <div style={{ marginTop: '56px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
        <Link
          to="/travel"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--text-3)',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textDecoration: 'none',
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-3)'; }}
        >
          ← TRAVEL
        </Link>
      </div>
    </div>
  );
}
